import * as XLSX from "xlsx";
import { WasteSortingExcelUploadModalOutput } from "../types/types";

type ParseResult = { success: true; data: WasteSortingExcelUploadModalOutput[] } | { success: false; errors: string[] };

function parseDate(value: unknown): Date | null {
  if (value == null || value === "") return null;

  // Excel serial date
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;

    const d = new Date(parsed.y, parsed.m - 1, parsed.d);
    return isNaN(d.getTime()) ? null : d;
  }

  const s = String(value).trim();
  if (!s) return null;

  // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
  const parts = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (parts) {
    const [, day, month, year] = parts;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  }

  // ISO / native-compatible formats
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return value;
  if (value == null || value === "") return 0;

  // Allow decimal comma as well
  const normalized = String(value).trim().replace(",", ".");
  return Number(normalized);
}

export function parseWasteSortingExcel(buffer: ArrayBuffer): ParseResult {
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  // Read rows as arrays so we are independent of header language
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: "",
    blankrows: false,
  });

  // Template layout:
  // row 1 = intro text
  // row 2 = help text
  // row 3 = headers
  // row 4+ = data
  const dataRows = rows.slice(3);

  if (dataRows.length === 0) {
    return { success: false, errors: ["The file contains no data rows."] };
  }

  // We only require that the sheet has at least 5 columns in the expected order
  const headerRow = rows[2] ?? [];
  if (!Array.isArray(headerRow) || headerRow.length < 5) {
    return {
      success: false,
      errors: ["The file does not match the expected template layout."],
    };
  }

  const importRowErrors: string[] = [];
  const output: WasteSortingExcelUploadModalOutput[] = [];

  dataRows.forEach((row, index) => {
    const cells = Array.isArray(row) ? row : [];
    const rowNumber = index + 4; // Excel row number

    const wasteTypeLabel = cells[0];
    const label = cells[1];
    const quantityValue = cells[2];
    const unit = cells[3];
    const pickupDateValue = cells[4];

    const quantity = toNumber(quantityValue);

    const isEmptyRow =
      (wasteTypeLabel == null || wasteTypeLabel === "") &&
      (label == null || label === "") &&
      (quantityValue == null || quantityValue === "") &&
      (unit == null || unit === "") &&
      (pickupDateValue == null || pickupDateValue === "");

    if (isEmptyRow) return;

    const rowErrors: string[] = [];

    if (!wasteTypeLabel) rowErrors.push("missing waste fraction");
    if (!label) rowErrors.push("missing name");
    if (quantityValue === "" || quantityValue == null) {
      rowErrors.push("missing quantity");
    } else if (isNaN(quantity) || quantity < 0) {
      rowErrors.push(`invalid quantity "${quantityValue}"`);
    }

    let parsedDate: Date | null = null;
    if (pickupDateValue !== undefined && pickupDateValue !== null && pickupDateValue !== "") {
      parsedDate = parseDate(pickupDateValue);
      if (!parsedDate) {
        rowErrors.push(`invalid date "${pickupDateValue}"`);
      }
    }

    // Accept kg or ton (template offers both). Convert ton → kg since output expects kg.
    const normalizedUnit = unit ? String(unit).trim().toLowerCase() : "kg";
    let weight = quantity;
    if (normalizedUnit === "kg") {
      weight = quantity;
    } else if (
      normalizedUnit === "ton" ||
      normalizedUnit === "tons" ||
      normalizedUnit === "tonne" ||
      normalizedUnit === "tonnes"
    ) {
      weight = quantity * 1000;
    } else {
      rowErrors.push(`unsupported unit "${unit}" (expected kg or ton)`);
    }

    if (rowErrors.length > 0) {
      importRowErrors.push(`Row ${rowNumber}: ${rowErrors.join(", ")}`);
      return;
    }

    output.push({
      label: String(label).trim(),
      wasteTypeLabel: String(wasteTypeLabel).trim(),
      weight,
      pickupDate: parsedDate || undefined,
    });
  });

  if (importRowErrors.length > 0) {
    return { success: false, errors: importRowErrors };
  }

  if (output.length === 0) {
    return { success: false, errors: ["No valid rows found in the file."] };
  }

  return { success: true, data: output };
}
